/**
 * Browser language detection utility for KernLogic
 * Detects user's preferred language from browser settings for first-time users
 */

// Supported languages in KernLogic
const SUPPORTED_LANGUAGES = ['en', 'es', 'de', 'pt'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Language mapping from browser locale codes to KernLogic supported languages
 * Maps various locale formats (en-US, en-GB, etc.) to our base language codes
 */
const LANGUAGE_MAPPING: Record<string, SupportedLanguage> = {
  // English variants
  en: 'en',
  'en-US': 'en',
  'en-GB': 'en',
  'en-CA': 'en',
  'en-AU': 'en',
  'en-NZ': 'en',
  'en-IE': 'en',
  'en-IN': 'en',
  'en-SG': 'en',
  'en-ZA': 'en',

  // Spanish variants
  es: 'es',
  'es-ES': 'es',
  'es-MX': 'es',
  'es-AR': 'es',
  'es-CO': 'es',
  'es-CL': 'es',
  'es-PE': 'es',
  'es-VE': 'es',
  'es-UY': 'es',
  'es-EC': 'es',
  'es-BO': 'es',
  'es-PY': 'es',
  'es-CR': 'es',
  'es-PA': 'es',
  'es-GT': 'es',
  'es-HN': 'es',
  'es-NI': 'es',
  'es-SV': 'es',
  'es-DO': 'es',
  'es-CU': 'es',
  'es-PR': 'es',

  // German variants
  de: 'de',
  'de-DE': 'de',
  'de-AT': 'de',
  'de-CH': 'de',
  'de-LU': 'de',
  'de-LI': 'de',

  // Portuguese variants
  pt: 'pt',
  'pt-BR': 'pt',
  'pt-PT': 'pt',
  'pt-AO': 'pt',
  'pt-MZ': 'pt',
  'pt-GW': 'pt',
  'pt-CV': 'pt',
  'pt-ST': 'pt',
  'pt-TL': 'pt',
};

/**
 * Detects the user's preferred language from browser settings
 * @returns The detected language code or 'en' as fallback
 */
export function detectBrowserLanguage(): SupportedLanguage {
  // Default fallback
  const fallback: SupportedLanguage = 'en';

  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return fallback;
  }

  try {
    // Get browser languages in order of preference
    const browserLanguages: string[] = [];

    // Primary language from navigator.language
    if (navigator.language) {
      browserLanguages.push(navigator.language);
    }

    // Additional languages from navigator.languages (if available)
    if (navigator.languages && Array.isArray(navigator.languages)) {
      browserLanguages.push(...navigator.languages);
    }

    // Legacy support for older browsers
    const legacyLanguage = (navigator as any).userLanguage || (navigator as any).browserLanguage;
    if (legacyLanguage) {
      browserLanguages.push(legacyLanguage);
    }

    // Find the first supported language
    for (const browserLang of browserLanguages) {
      if (!browserLang) continue;

      // Normalize the language code (convert to lowercase)
      const normalizedLang = browserLang.toLowerCase();

      // Direct match in our mapping
      if (LANGUAGE_MAPPING[normalizedLang]) {
        return LANGUAGE_MAPPING[normalizedLang];
      }

      // Try matching just the language part (before the first dash)
      const baseLang = normalizedLang.split('-')[0];
      if (LANGUAGE_MAPPING[baseLang]) {
        return LANGUAGE_MAPPING[baseLang];
      }
    }

    return fallback;
  } catch {
    // If any error occurs during detection, fall back to English
    return fallback;
  }
}

/**
 * Gets the initial language for the application
 * Priority: localStorage > browser detection > fallback to English
 * @returns The language code to use for initialization
 */
export function getInitialLanguage(): SupportedLanguage {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return 'en';
  }

  try {
    // First priority: Check if user has previously selected a language
    const savedLanguage = localStorage.getItem('kernlogic-language');
    if (savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage as SupportedLanguage)) {
      return savedLanguage as SupportedLanguage;
    }

    // Second priority: Detect from browser if no saved preference
    const browserLanguage = detectBrowserLanguage();

    // Optional: Save the detected language to localStorage for future visits
    // This way, the user won't see language changes between sessions unless they explicitly change it
    localStorage.setItem('kernlogic-language', browserLanguage);

    return browserLanguage;
  } catch {
    return 'en';
  }
}

/**
 * Checks if a language code is supported by KernLogic
 * @param language - The language code to check
 * @returns True if the language is supported
 */
export function isSupportedLanguage(language: string): language is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
}

/**
 * Gets all supported language codes
 * @returns Array of supported language codes
 */
export function getSupportedLanguages(): readonly SupportedLanguage[] {
  return SUPPORTED_LANGUAGES;
}
